import type { ZodType } from "zod";

import { logger } from "@/lib/logger";

export interface KeyedStore<TValue> {
  clear: () => void;
  read: () => TValue | null;
  write: (value: TValue) => void;
}

interface CreateKeyedStoreOptions {
  storage?: Storage;
}

function resolveStorage(override?: Storage): Storage | null {
  if (override) return override;
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage;
  } catch (error) {
    logger.warn("localStorage is not available", { error });
    return null;
  }
}

export function createKeyedStore<TValue>(
  key: string,
  schema: ZodType<TValue>,
  options: CreateKeyedStoreOptions = {},
): KeyedStore<TValue> {
  const storage = resolveStorage(options.storage);

  return {
    clear() {
      if (!storage) return;
      try {
        storage.removeItem(key);
      } catch (error) {
        logger.warn("Failed to clear storage key", { error, key });
      }
    },
    read() {
      if (!storage) return null;

      try {
        const raw = storage.getItem(key);
        if (!raw) return null;

        const parsed: unknown = JSON.parse(raw);
        const result = schema.safeParse(parsed);

        if (!result.success) {
          logger.warn("Stored value failed schema validation; discarding", {
            issues: result.error.issues,
            key,
          });
          storage.removeItem(key);
          return null;
        }

        return result.data;
      } catch (error) {
        logger.warn("Failed to read storage key", { error, key });
        return null;
      }
    },
    write(value) {
      if (!storage) return;

      try {
        storage.setItem(key, JSON.stringify(value));
      } catch (error) {
        logger.warn("Failed to write storage key", { error, key });
      }
    },
  };
}
